import { find } from 'lodash';
import React, { useContext } from 'react';
import styled from 'styled-components/macro';
import { assertNotNull } from '../../../../../../helpers/nullHelpers';
import { VerticalCheckboxField } from '../../../../../../shared/form/inputs/CheckboxField';
import { TextInputField } from '../../../../../../shared/form/inputs/TextInputField';
import { ReadOnlyField } from '../../../../../../shared/form/ReadOnlyField';
import { DesignMaterialsCalculationReadOnlyField } from '../../../../../designs/design-form/calculations/DesignMaterialsCalculationReadOnlyField';
import { DesignMaterialsCalculationsRequest } from '../../../../../designs/design-form/calculations/materials/designMaterialsCalculationsResponse';
import { QuoteOrDesignFormContext } from '../../../QuoteOrDesignFormContext';
import { roomFieldName } from '../../../quoteOrDesignFormFieldNameHelpers';
import { RoomFormModel } from '../../roomFormModel';
import { RoomAutocompleteItem } from '../../roomTypes';
import { RoomFloorConstructionField } from '../RoomFloorConstructionField';
import { RoomNameField } from '../RoomNameField';

// The CAD fields for a Room are:
// * Room Name
// * Floor Construction
// * Drawn Length
// * Vertical Runback
// * Oversupply Length
// * Total Cut Length
// * Total Supply Length
// * Peripheral Area
// * Turn Panel Quantity
// * Straight Panel Quantity
// * Plate Area
// * Panels for Unheated Area

type Props = {
  unitIndex: number;
  manifoldIndex: number;
  roomIndex: number;
  isWithinRadiatorManifold: boolean;
  room: RoomFormModel;
  handleSelectionOfRoomName: (selectedItem: RoomAutocompleteItem) => void;
  designMaterialsCalculationsRequest?: DesignMaterialsCalculationsRequest;
};

export const RoomFieldsCad = ({
  unitIndex,
  manifoldIndex,
  roomIndex,
  isWithinRadiatorManifold,
  room,
  handleSelectionOfRoomName,
  designMaterialsCalculationsRequest,
}: Props) => {
  const designMaterialsCalculationsResponse =
    designMaterialsCalculationsRequest && designMaterialsCalculationsRequest.response;
  const designMaterialsCalculationsInProgress =
    designMaterialsCalculationsRequest != null && designMaterialsCalculationsRequest.inProgress;
  const designMaterialsCalculationsError =
    (designMaterialsCalculationsRequest && designMaterialsCalculationsRequest.error) || null;

  const roomDesignMaterialsCalculationsResult =
    (designMaterialsCalculationsResponse &&
      find(
        designMaterialsCalculationsResponse.rooms,
        r => r.unitIndex === unitIndex && r.manifoldIndex === manifoldIndex && r.roomIndex === roomIndex,
      )) ||
    null;

  const formContext = assertNotNull(useContext(QuoteOrDesignFormContext));
  const formRooms = assertNotNull(formContext.formProps.values.units[unitIndex].manifolds[manifoldIndex].rooms);
  const floorConstruction = formRooms[roomIndex].floorConstruction;

  const totalCutLength = room.designRoomDetails
    ? Number(room.designRoomDetails.drawnLengthInMetres) + 2 * Number(room.designRoomDetails.verticalRunbackInMetres)
    : null;

  return (
    <>
      <RoomNameField
        unitIndex={unitIndex}
        manifoldIndex={manifoldIndex}
        roomIndex={roomIndex}
        handleSelectionOfRoomName={handleSelectionOfRoomName}
      />
      <RoomFloorConstructionField
        unitIndex={unitIndex}
        manifoldIndex={manifoldIndex}
        roomIndex={roomIndex}
        isWithinRadiatorManifold={isWithinRadiatorManifold}
      />
      <TextInputField
        name={roomFieldName(unitIndex, manifoldIndex, roomIndex, 'designRoomDetails.drawnLengthInMetres')}
        label="Drawn Length (m)"
      />
      <TextInputField
        name={roomFieldName(unitIndex, manifoldIndex, roomIndex, 'designRoomDetails.verticalRunbackInMetres')}
        label="Vertical Runback (m)"
      />
      <TextInputField
        name={roomFieldName(unitIndex, manifoldIndex, roomIndex, 'designRoomDetails.oversupplyLengthInMetres')}
        label="Oversupply Length (m)"
      />
      <ReadOnlyField value={totalCutLength ? totalCutLength.toLocaleString() : ''} label="Total Cut Length (M)" />
      <DesignMaterialsCalculationReadOnlyField
        label="Supplied Coil Length (m)"
        designMaterialsCalculationsInProgress={designMaterialsCalculationsInProgress}
        designMaterialsCalculationsError={designMaterialsCalculationsError}
        value={roomDesignMaterialsCalculationsResult?.suppliedCoilLengthInMetres?.toLocaleString() || null}
      />
      <TextInputField
        name={roomFieldName(unitIndex, manifoldIndex, roomIndex, 'designRoomDetails.peripheralAreaInMetresSquared')}
        label={
          <>
            Peripheral Area (m<sup>2</sup>)
          </>
        }
        disabled={!floorConstruction?.requiresPeripheralArea}
      />
      <TextInputField
        name={roomFieldName(unitIndex, manifoldIndex, roomIndex, 'designRoomDetails.turnPanelQuantity')}
        label="Turn Panel Qty"
        disabled={!floorConstruction?.requiresTurnPanelQuantity}
      />
      <TextInputField
        name={roomFieldName(unitIndex, manifoldIndex, roomIndex, 'designRoomDetails.straightPanelQuantity')}
        label="Straight Panel Qty"
        disabled={!floorConstruction?.requiresStraightPanelQuantity}
      />
      <TextInputField
        name={roomFieldName(unitIndex, manifoldIndex, roomIndex, 'designRoomDetails.plateAreaInMetresSquared')}
        label={
          <>
            Plate Area (m<sup>2</sup>)
          </>
        }
        disabled={!floorConstruction?.requiresPlateArea}
      />
      <MinWidthVerticalCheckboxField
        name={roomFieldName(unitIndex, manifoldIndex, roomIndex, 'designRoomDetails.hasPanelsForUnheatedArea')}
        label="Panels for Unheated Area?"
      />
      <DesignMaterialsCalculationReadOnlyField
        label="Levelling Compound"
        designMaterialsCalculationsInProgress={designMaterialsCalculationsInProgress}
        designMaterialsCalculationsError={designMaterialsCalculationsError}
        value={roomDesignMaterialsCalculationsResult?.selfLevellingCompoundQuantity?.toLocaleString() || null}
      />
    </>
  );
};

const MinWidthVerticalCheckboxField = styled(VerticalCheckboxField)`
  min-width: 140px;
`;
